import catalog from './ArgentinaChannelCatalog.json';
import { getRemoteSnapshot, remoteCall } from '../cast/RemoteSession.ts';
import { sourceHttpClient } from '../core/SourceHttp.ts';
import { assertPublicUrl } from '../network/SourceRegistry.ts';
import type { LiveOption } from './NowFutbol.ts';

export interface ArgentinaChannel {
  id: string;
  name: string;
  category: 'aire' | 'noticias' | 'streaming' | 'deportes';
  brandBg: string;
  brandColor: string;
  logoText: string;
  logoUrl?: string;
  videoId?: string;
  isLive: boolean;
  statusKnown?: boolean;
  currentProgram?: string;
  ytChannelId?: string;
  pageUrl?: string;
  options?: LiveOption[];
  player?: 'telefe-official';
}
const SOURCE_ID = 'argentinatv';
const CATALOG_URL = 'https://raw.githubusercontent.com/iamdecherre/fuentes/main/argentina-channels.json?v=3';
type CatalogChannel = Pick<ArgentinaChannel, 'id' | 'name' | 'category' | 'ytChannelId' | 'pageUrl' | 'logoUrl' | 'options' | 'player'>;
function safeOptions(input: unknown): LiveOption[] {
  if (!Array.isArray(input)) return [];
  return input.slice(0, 10).flatMap(option => {
    try {
      if (typeof option?.url !== 'string' || typeof option.name !== 'string') return [];
      const url = assertPublicUrl(option.url);
      return url.protocol === 'https:' ? [{name:option.name.slice(0,100),url:url.href}] : [];
    } catch { return []; }
  });
}
interface LiveInfo { video?: string; vivo?: boolean; embed?: boolean; titulo?: string }
export const BASE_ARGENTINA_CHANNELS = catalog as CatalogChannel[];
let cached: { until: number; channels: ArgentinaChannel[] } | undefined;
let pending: Promise<ArgentinaChannel[]> | undefined;
export function clearArgentinaLiveStatus(): void { cached = undefined; }

export function parseArgentinaStatus(entries: CatalogChannel[], input: unknown): ArgentinaChannel[] {
  const values = input && typeof input === 'object' && 'vivos' in input ? (input as { vivos: unknown }).vivos : {};
  const map = values && typeof values === 'object' ? values as Record<string, LiveInfo> : {};
  return entries.map(base => {
    const info = base.player ? undefined : map[base.id];
    const video = typeof info?.video === 'string' && /^[\w-]{11}$/.test(info.video) ? info.video : undefined;
    const isLive = info?.vivo === true && info.embed === true && !!video;
    return { ...base, brandBg: '#18181b', brandColor: '#ffffff', logoText: base.name,
      isLive, statusKnown: typeof info?.vivo === 'boolean', videoId: isLive ? video : undefined,
      logoUrl: video ? `https://i.ytimg.com/vi/${video}/hqdefault.jpg` : base.logoUrl,
      currentProgram: base.player ? 'Reproductor oficial de Telefe' : isLive && typeof info?.titulo === 'string' ? info.titulo.slice(0, 220) : base.options?.length ? 'Señal de Argentina TV' : 'Sin emisión confirmada',
    };
  }).sort((a, b) => Number(b.isLive) - Number(a.isLive) || a.name.localeCompare(b.name, 'es'));
}
export function fetchArgentinaLiveStatus(): Promise<ArgentinaChannel[]> {
  if(getRemoteSnapshot())return remoteCall('live.argentina',{});
  if (cached && cached.until > Date.now()) return Promise.resolve(cached.channels);
  if (pending) return pending;
  pending = (async () => {
    let entries = BASE_ARGENTINA_CHANNELS;
    try {
      const response = await sourceHttpClient.text({ sourceId: SOURCE_ID, url: CATALOG_URL, method: 'GET',
        responseType: 'text', corsOpen: true, credentials: 'omit', timeoutMs: 4000, cache: { ttlMs: 3600000, scope: 'public' } });
      const rows: unknown = JSON.parse(response.body);
      if (response.status === 200 && Array.isArray(rows)) {
        const valid = rows.filter((row): row is CatalogChannel => !!row && typeof row === 'object' &&
          /^[a-z0-9-]{1,64}$/.test(row.id) && (row.id !== 'telefe' || row.player === 'telefe-official') && typeof row.name === 'string' &&
          row.name.length < 100 && ['aire','noticias','streaming','deportes'].includes(row.category) &&
          ((row.id === 'telefe' && row.player === 'telefe-official') || safeOptions(row.options).length > 0 || (typeof row.ytChannelId === 'string' && /^UC[\w-]{22}$/.test(row.ytChannelId)) ||
          (typeof row.pageUrl === 'string' && /^https:\/\/www\.teleonline\.tv\/canal\/[a-z0-9-]+\/$/.test(row.pageUrl))));
        if (valid.length) entries = [...new Map(valid.slice(0, 150).map(row => [row.id, {
          id: row.id, name: row.name, category: row.category, ytChannelId: row.ytChannelId,
          options: row.id === 'telefe' ? [] : safeOptions(row.options),
          player: row.id === 'telefe' ? 'telefe-official' as const : undefined,
          pageUrl: row.pageUrl && /^https:\/\/www\.teleonline\.tv\/canal\/[a-z0-9-]+\/$/.test(row.pageUrl) ? row.pageUrl : undefined,
          logoUrl: row.logoUrl && (/^https:\/\/i\.ytimg\.com\/vi\/[\w-]{11}\/hqdefault\.jpg$/.test(row.logoUrl) || row.logoUrl === 'https://angulismotv.pages.dev/assets/logos/TELEFE.png') ? row.logoUrl : undefined,
        }])).values()];
      }
    } catch { /* Bundled catalog remains available offline. */ }
    if (!entries.some(row => row.id === 'telefe')) {
      const telefe = BASE_ARGENTINA_CHANNELS.find(row => row.id === 'telefe');
      if (telefe) entries = [...entries, telefe];
    }
    let status: unknown = {};
    try {
      const response = await sourceHttpClient.text({ sourceId: SOURCE_ID,
        url: 'https://hiox.com.ar/tv/api/vivos.php?r=ar', method: 'GET', responseType: 'text',
        credentials: 'omit', timeoutMs: 8000, cache: { ttlMs: 120000, scope: 'public' } });
      if (response.status === 200) status = JSON.parse(response.body);
    } catch { /* Unknown status must never be advertised as live. */ }
    const channels = parseArgentinaStatus(entries, status);
    await Promise.all(channels.filter(channel => channel.pageUrl && !channel.statusKnown).map(async channel => {
      try {
        const response = await sourceHttpClient.text({ sourceId: SOURCE_ID, url: channel.pageUrl!,
          method: 'GET', responseType: 'text', credentials: 'omit', timeoutMs: 8000, cache: { ttlMs: 120000, scope: 'public' } });
        if (response.status !== 200) return;
        const html = response.body.replaceAll('\\/', '/');
        const id = html.match(/youtube\.com\/embed\/([\w-]{11})/)?.[1];
        if (id) {
          channel.videoId = id;
          channel.logoUrl = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
          channel.currentProgram = 'Señal de YouTube · estado sin confirmar';
        }
      } catch { /* Keep unavailable channels explicit. */ }
    }));
    cached = { until: Date.now() + 120000, channels };
    return channels;
  })().finally(() => { pending = undefined; });
  return pending;
}
